import React, { useEffect, useState } from "react";

interface AssignUser {
  id: string;
  name: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  // Step 1 picks the single crew lead; step 2 ticks the rest of the crew.
  onSubmit: (payload: { crewLeadId: string; memberIds: string[] }) => void;
  users: AssignUser[];
  // Pre-fill when editing an already-assigned site.
  initialCrewLeadId?: string;
  initialMemberIds?: string[];
}

// Crew assignment flow:
//   Step 0 — SUMMARY (only when a crew is already assigned): shows the current
//            crew lead + crew, with Cancel and Edit. Edit drops into step 1.
//   Step 1 — choose ONE Crew Lead (can certify the site).
//   Step 2 — tick MULTIPLE crew members (Tower Crew: can edit, cannot certify),
//            with the crew lead excluded from the list.
// The whole flow can be re-opened to edit the crew until the wizard completes.
export default function SMRTCOPAssignCrewModal({
  isOpen,
  onClose,
  onSubmit,
  users,
  initialCrewLeadId = "",
  initialMemberIds = [],
}: Props) {
  const [step, setStep] = useState<0 | 1 | 2>(1);
  const [crewLeadId, setCrewLeadId] = useState(initialCrewLeadId);
  const [memberIds, setMemberIds] = useState<string[]>(initialMemberIds);

  // Re-seed from the site being edited every time the modal opens. Open on the
  // summary step when a crew already exists; otherwise start fresh at step 1.
  const seedKey = `${initialCrewLeadId}|${initialMemberIds.join(",")}`;
  useEffect(() => {
    if (isOpen) {
      setStep(initialCrewLeadId ? 0 : 1);
      setCrewLeadId(initialCrewLeadId);
      setMemberIds(initialMemberIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, seedKey]);

  if (!isOpen) return null;

  const nameOf = (id: string) => users.find((u) => u.id === id)?.name || id;

  const goToCrewStep = () => {
    if (!crewLeadId) {
      alert("Please select a crew lead");
      return;
    }
    // The lead is never also listed as ordinary crew.
    setMemberIds((prev) => prev.filter((id) => id !== crewLeadId));
    setStep(2);
  };

  const toggleMember = (id: string) => {
    setMemberIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleFinish = () => {
    onSubmit({
      crewLeadId,
      memberIds: memberIds.filter((id) => id !== crewLeadId),
    });
  };

  // Step 2 list excludes whoever was chosen as crew lead.
  const crewOptions = users.filter((u) => u.id !== crewLeadId);

  return (
    <div className="smrtdocs-modal-overlay">
      <div className="smrtdocs-modal" style={{ width: "500px" }}>
        <div className="smrtdocs-modal-header">
          <h2>
            {step === 0
              ? "Assigned Crew"
              : step === 1
                ? "Assign Crew Lead"
                : "Assign Crew"}
          </h2>
          <button className="btn-cancel" onClick={onClose}>
            Close
          </button>
        </div>

        {step === 0 ? (
          <>
            <div className="smrtdocs-modal-body">
              <div style={{ marginBottom: "14px" }}>
                <div
                  style={{
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    color: "#888",
                    marginBottom: "4px",
                  }}
                >
                  Crew Lead
                </div>
                <div style={{ fontSize: "0.95rem" }}>
                  {crewLeadId ? nameOf(crewLeadId) : "—"}
                </div>
              </div>

              <div>
                <div
                  style={{
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    color: "#888",
                    marginBottom: "4px",
                  }}
                >
                  Crew
                </div>
                {memberIds.length === 0 ? (
                  <div style={{ fontSize: "0.95rem", color: "#999" }}>
                    No additional crew assigned.
                  </div>
                ) : (
                  <ul style={{ margin: 0, paddingLeft: "18px" }}>
                    {memberIds.map((id) => (
                      <li key={id} style={{ fontSize: "0.95rem" }}>
                        {nameOf(id)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div
              style={{
                padding: "10px",
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
              }}
            >
              <button className="btn-cancel" onClick={onClose}>
                Cancel
              </button>
              <button className="btn-save" onClick={() => setStep(1)}>
                Edit
              </button>
            </div>
          </>
        ) : step === 1 ? (
          <>
            <div className="smrtdocs-modal-body">
              <select
                className="manage-input"
                value={crewLeadId}
                onChange={(e) => setCrewLeadId(e.target.value)}
                style={{ width: "100%" }}
              >
                <option value="">Select Crew Lead</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ padding: "10px", textAlign: "right" }}>
              <button className="btn-save" onClick={goToCrewStep}>
                Next
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="smrtdocs-modal-body">
              <div
                style={{
                  fontSize: "0.85rem",
                  color: "#ccc",
                  marginBottom: "10px",
                }}
              >
                Select the crew members working this site. The crew lead is
                already assigned and isn&apos;t listed here.
              </div>

              <div
                style={{
                  maxHeight: "260px",
                  overflowY: "auto",
                  border: "1px solid #444",
                  borderRadius: "6px",
                }}
              >
                {crewOptions.length === 0 ? (
                  <div style={{ padding: "12px", color: "#999" }}>
                    No other crew available to assign.
                  </div>
                ) : (
                  crewOptions.map((u) => (
                    <label
                      key={u.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "8px 12px",
                        borderBottom: "1px solid #333",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={memberIds.includes(u.id)}
                        onChange={() => toggleMember(u.id)}
                      />
                      <span>{u.name}</span>
                    </label>
                  ))
                )}
              </div>
            </div>

            <div
              style={{
                padding: "10px",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <button className="btn-cancel" onClick={() => setStep(1)}>
                Back
              </button>
              <button className="btn-save" onClick={handleFinish}>
                Assign
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
